import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth, AuthProvider } from '../contexts/AuthContext';
import { Box, Typography, Card, CardContent, Button, useTheme, useMediaQuery, Alert, Dialog, Divider } from '@mui/material'; // Added Divider
import { supabase } from '../supabaseClient';
import { styled } from '@mui/system';
import { canvasWorkflows } from '../workflows/canvasWorkflows';
import { useNavigate } from 'react-router-dom';
import { roomTypeTranslations, styleTranslations, colorThemeTranslations } from '../constants/translations';
import { TutorialTip } from './Onboarding';
import SampleProjectsGallery from './SampleProjectsGallery';

const WorkflowCard = styled(Card)(({ theme, selected }) => ({
  border: 'none',
  borderRadius: '16px',
  transform: 'translateZ(0)',
  willChange: 'transform',
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(26, 26, 38, 0.95)'
    : '#fdfdfd',
  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
  position: 'relative',
  overflow: 'visible',
  boxShadow: selected 
    ? `0 10px 30px rgba(0, 74, 173, 0.3)`
    : theme.palette.mode === 'dark'
      ? '0 8px 24px rgba(0, 0, 0, 0.25)'
      : '0 6px 20px rgba(0, 0, 0, 0.08)',
  backdropFilter: 'blur(10px)',
  border: selected 
    ? `2px solid #004aad` 
    : theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid rgba(0, 74, 173, 0.15)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    background: theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, rgba(0, 74, 173, 0.1) 0%, transparent 100%)`
      : `linear-gradient(135deg, rgba(203, 241, 153, 0.2) 0%, rgba(162, 195, 164, 0.1) 100%)`,
    opacity: 1,
    zIndex: 0,
  },
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: selected 
      ? `0 14px 40px rgba(0, 74, 173, 0.4)`
      : `0 12px 30px rgba(0, 74, 173, 0.2)`,
    border: selected 
      ? `2px solid #004aad` 
      : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(0, 74, 173, 0.5)' : '#004aad'}`,
  }
}));

const SelectButton = styled(Button)(({ theme, selected }) => ({
  background: selected 
    ? '#004aad'
    : 'transparent',
  color: selected 
    ? '#fdfdfd' 
    : '#004aad',
  backdropFilter: 'blur(8px)',
  transition: 'all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1)',
  padding: '8px 20px',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.9rem',
  letterSpacing: '0.3px',
  border: selected 
    ? `1px solid #004aad`
    : `1px solid rgba(0, 74, 173, 0.5)`,
  boxShadow: selected 
    ? `0 4px 12px rgba(0, 74, 173, 0.3)`
    : 'none',
  '&:hover': {
    background: selected 
      ? '#003580'
      : 'rgba(0, 74, 173, 0.08)',
    borderColor: '#004aad',
    boxShadow: `0 6px 16px rgba(0, 74, 173, 0.25)`,
    transform: 'translateY(-2px)',
  }
}));

const WorkflowDirectoryContent = React.memo(function WorkflowDirectoryContent() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [deploymentId, setDeploymentId] = useState('');
  const [mode, setMode] = useState('interior');
  const [session, setSession] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [showTutorials, setShowTutorials] = useState(false);
  const [sampleProjectsOpen, setSampleProjectsOpen] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      
      if (session?.user) {
        // Get user profile to check preferences
        const { data } = await supabase
          .from('profiles')
          .select('preferences, experience_level, dismissed_tutorials')
          .eq('id', session.user.id)
          .single();
          
        setUserProfile(data);
        
        // Show tutorials based on user preferences
        if (data?.preferences?.tutorials && 
            (!data.dismissed_tutorials || !data.dismissed_tutorials.workflowIntro)) {
          setShowTutorials(true);
        }
      }
    };
    checkSession();
  }, []);

  const handleWorkflowSelect = useCallback((workflow) => {
    if (!session) {
      navigate('/signup', { 
        state: { 
          returnUrl: workflow.path || `/canvas-editor?workflow=${workflow.id}`,
          message: 'Bu iş akışını kullanmak için lütfen giriş yapın veya kaydolun'
        }
      });
      return;
    }

    setSelectedWorkflow(workflow);
    setDeploymentId(workflow.deploymentId);
    setMode(workflow.mode);
    
    // Special handling for video generator
    if (workflow.id === 'video-generator') {
      navigate('/video-generator');
      return;
    }
    
    // If workflow has a specific path, navigate there instead of canvas editor
    if (workflow.path) {
      navigate(workflow.path);
    } else {
      navigate(`/canvas-editor?workflow=${workflow.id}`);
    }
  }, [navigate, session]);
  
  const handleOpenSampleProjects = useCallback(() => {
    setSampleProjectsOpen(true);
  }, []);
  
  const handleCloseSampleProjects = useCallback(() => {
    setSampleProjectsOpen(false);
  }, []);
  
  const handleSelectSampleProject = useCallback((project) => {
    setSampleProjectsOpen(false);
    
    // Navigate to canvas editor with the selected sample project
    navigate(`/canvas-editor?workflow=${project.workflow || 'interior-redesign'}`, {
      state: {
        sampleProject: project
      }
    });
  }, [navigate]);

  const currentWorkflowConfig = useMemo(() => 
    selectedWorkflow ? {
      deploymentId: selectedWorkflow.deploymentId,
      mode: selectedWorkflow.mode,
      title: selectedWorkflow.title
    } : null
  , [selectedWorkflow]);

  return (
    <Box sx={{ 
      maxWidth: '1600px',
      width: '100%',
      margin: '0 auto', 
      padding: { 
        xs: '24px 16px', 
        sm: '40px 30px', 
        md: '50px 40px',
        lg: '60px 40px'
      },
      position: 'relative',
      backgroundColor: theme => theme.palette.mode === 'dark'
        ? 'rgba(26, 26, 38, 0.95)'
        : '#fdfdfd',
      borderRadius: { xs: '16px', sm: '20px' },
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      border: theme => theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.1)'
        : '1px solid rgba(0, 74, 173, 0.1)',
      overflow: 'hidden',
      backdropFilter: 'blur(10px)',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: theme => theme.palette.mode === 'dark'
          ? `linear-gradient(135deg, rgba(0, 74, 173, 0.15) 0%, transparent 100%),
             radial-gradient(circle at 25% 25%, rgba(162, 195, 164, 0.1) 0%, transparent 70%)`
          : `linear-gradient(135deg, rgba(203, 241, 153, 0.1) 0%, transparent 100%),
             radial-gradient(circle at 75% 75%, rgba(0, 74, 173, 0.1) 0%, transparent 70%)`,
        opacity: 1,
        zIndex: 0,
      }
    }}>
      {/* Welcome Tutorial Tip */}
      {showTutorials && (
        <Box sx={{ position: 'relative', zIndex: 10, mb: 2, mt: 0, ml: 'auto', mr: 0, width: '45%' }}>
          <TutorialTip
            id="workflowIntro"
            title="Hoş Geldiniz!"
            description="Burası iş akışı seçim sayfasıdır. Yapay zeka destekli mimari görselleştirme araçlarımızdan birini seçerek başlayabilirsiniz."
            placement="bottom"
            onDismiss={() => setShowTutorials(false)}
          >
            <Box sx={{ width: '100%', height: 20 }} />
          </TutorialTip>
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 5, position: 'relative', zIndex: 1 }}>
        <Box 
          sx={{ 
            position: 'absolute', 
            top: { xs: -40, sm: -60 }, 
            left: '50%', 
            transform: 'translateX(-50%)',
            width: { xs: 200, sm: 300 },
            height: { xs: 200, sm: 300 },
            background: theme => theme.palette.mode === 'dark'
              ? 'radial-gradient(circle, rgba(0, 74, 173, 0.2) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(203, 241, 153, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
            zIndex: -1,
            filter: 'blur(20px)'
          }} 
        />
        
        <Typography 
          variant="h3" 
          sx={{ 
            mb: 3, 
            fontWeight: 800,
            textAlign: 'center',
            color: theme => theme.palette.mode === 'dark' 
              ? '#fdfdfd'
              : '#004aad',
            letterSpacing: '-0.02em',
            fontSize: { xs: '2.2rem', sm: '2.8rem' },
            position: 'relative',
            lineHeight: 1.2,
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -12,
              left: '50%',
              transform: 'translateX(-50%)',
              width: { xs: 100, sm: 120 },
              height: 4,
              background: theme => theme.palette.mode === 'dark'
                ? 'linear-gradient(90deg, #004aad, #3373c4)'
                : 'linear-gradient(90deg, #004aad, #A2C3A4)',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0, 74, 173, 0.3)'
            }
          }}
        >
          Mimari YZ Araçları
        </Typography>
        
        <Typography
          variant="subtitle1"
          sx={{
            mb: 4,
            textAlign: 'center',
            color: theme => theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.8)'
              : '#869d96',
            maxWidth: '700px',
            fontSize: { xs: '1rem', sm: '1.1rem' },
            lineHeight: 1.5
          }}
        >
          Profesyonel mimari görselleştirme için yapay zeka destekli araçlarımızı keşfedin
        </Typography>
        
        <Button
          variant="contained"
          disabled
          sx={{
            mb: 5,
            borderRadius: 2,
            padding: '12px 28px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            backgroundColor: 'rgba(0, 74, 173, 0.6)',
            color: '#fdfdfd',
            boxShadow: '0 4px 14px rgba(0, 74, 173, 0.2)',
            '&.Mui-disabled': {
              backgroundColor: 'rgba(0, 74, 173, 0.4)',
              color: 'rgba(253, 253, 253, 0.7)'
            },
            transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)'
          }}
        >
          Örnek Projeleri ve Şablonları Görüntüle (Yakında)
        </Button>
      </Box>
      
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(auto-fill, minmax(300px, 1fr))',
          md: 'repeat(auto-fill, minmax(280px, 1fr))',
          lg: 'repeat(auto-fill, minmax(260px, 1fr))'
        },
        gap: 3,
        mb: 6,
        position: 'relative',
        zIndex: 1
      }}>
        {/* Separate Staging Workflows */}
        {(() => {
          const virtualStagingWorkflow = canvasWorkflows.find(wf => wf.id === 'virtual-staging');
          const fastStagingWorkflow = canvasWorkflows.find(wf => wf.id === 'fast-staging');
          const otherWorkflows = canvasWorkflows.filter(wf => wf.id !== 'virtual-staging' && wf.id !== 'fast-staging');

          return (
            <>
              {/* Combined Staging Card */}
              {virtualStagingWorkflow && fastStagingWorkflow && (
                <WorkflowCard sx={{ gridColumn: { sm: 'span 2', lg: 'span 3' } }}> {/* Make it span more columns on larger screens */}
                  <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 3,
                      pb: 2,
                      borderBottom: theme => theme.palette.mode === 'dark' 
                        ? '1px solid rgba(255, 255, 255, 0.1)' 
                        : '1px solid rgba(0, 74, 173, 0.1)'
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        mr: 2,
                        background: theme => theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, rgba(0, 74, 173, 0.2) 0%, rgba(0, 74, 173, 0.1) 100%)'
                          : 'linear-gradient(135deg, rgba(203, 241, 153, 0.4) 0%, rgba(162, 195, 164, 0.2) 100%)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                      }}>
                        <Typography variant="h4" sx={{ 
                          color: theme => theme.palette.mode === 'dark' ? '#fdfdfd' : '#004aad',
                          filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                        }}>
                          🛋️
                        </Typography>
                      </Box>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 700,
                        color: theme => theme.palette.mode === 'dark' ? '#fdfdfd' : '#004aad',
                        letterSpacing: '-0.01em'
                      }}>
                        Sanal Dizayn Seçenekleri
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                      {/* Option 1: Hızlı Sanal Dizayn */}
                      <Box sx={{ 
                        flex: 1, 
                        display: 'flex', 
                        flexDirection: 'column',
                        backgroundColor: theme => theme.palette.mode === 'dark' 
                          ? 'rgba(26, 26, 38, 0.5)' 
                          : 'rgba(253, 253, 253, 0.7)',
                        borderRadius: '12px',
                        padding: 3,
                        border: theme => theme.palette.mode === 'dark'
                          ? '1px solid rgba(255, 255, 255, 0.05)'
                          : '1px solid rgba(0, 74, 173, 0.08)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)'
                      }}>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 700, 
                          mb: 2, 
                          color: theme => theme.palette.mode === 'dark' ? '#fdfdfd' : '#004aad',
                          fontSize: '1.1rem'
                        }}>
                          {fastStagingWorkflow.title}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          mb: 3, 
                          flexGrow: 1, // Make description take available space
                          color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#869d96',
                          lineHeight: 1.6,
                          fontSize: '0.95rem'
                        }}>
                          {fastStagingWorkflow.description}
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between', 
                          mt: 'auto',
                          pt: 2,
                          borderTop: theme => theme.palette.mode === 'dark'
                            ? '1px solid rgba(255, 255, 255, 0.05)'
                            : '1px solid rgba(0, 74, 173, 0.08)'
                        }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            backgroundColor: theme => theme.palette.mode === 'dark'
                              ? 'rgba(0, 74, 173, 0.15)'
                              : 'rgba(203, 241, 153, 0.3)',
                            border: theme => theme.palette.mode === 'dark'
                              ? '1px solid rgba(0, 74, 173, 0.3)'
                              : '1px solid rgba(162, 195, 164, 0.5)'
                          }}>
                            <Typography variant="caption" sx={{ 
                              color: theme => theme.palette.mode === 'dark' ? '#fdfdfd' : '#004aad', 
                              fontWeight: 600,
                              fontSize: '0.8rem'
                            }}>
                              {fastStagingWorkflow.credits} kredi
                            </Typography>
                          </Box>
                          <SelectButton
                            size="medium"
                            onClick={() => handleWorkflowSelect(fastStagingWorkflow)}
                          >
                            Seç
                          </SelectButton>
                        </Box>
                      </Box>

                      <Divider orientation={isMobile ? "horizontal" : "vertical"} flexItem sx={{ 
                        my: { xs: 2, md: 0 }, 
                        mx: { md: 0 },
                        borderColor: theme => theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(0, 74, 173, 0.1)'
                      }} />

                      {/* Option 2: Sanal Dizayn (Standard) */}
                      <Box sx={{ 
                        flex: 1, 
                        display: 'flex', 
                        flexDirection: 'column',
                        backgroundColor: theme => theme.palette.mode === 'dark' 
                          ? 'rgba(26, 26, 38, 0.5)' 
                          : 'rgba(253, 253, 253, 0.7)',
                        borderRadius: '12px',
                        padding: 3,
                        border: theme => theme.palette.mode === 'dark'
                          ? '1px solid rgba(255, 255, 255, 0.05)'
                          : '1px solid rgba(0, 74, 173, 0.08)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)'
                      }}>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 700, 
                          mb: 2, 
                          color: theme => theme.palette.mode === 'dark' ? '#fdfdfd' : '#004aad',
                          fontSize: '1.1rem'
                        }}>
                          {virtualStagingWorkflow.title}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          mb: 3, 
                          flexGrow: 1, 
                          color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#869d96',
                          lineHeight: 1.6,
                          fontSize: '0.95rem'
                        }}>
                          {virtualStagingWorkflow.description}
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between', 
                          mt: 'auto',
                          pt: 2,
                          borderTop: theme => theme.palette.mode === 'dark'
                            ? '1px solid rgba(255, 255, 255, 0.05)'
                            : '1px solid rgba(0, 74, 173, 0.08)'
                        }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            backgroundColor: theme => theme.palette.mode === 'dark'
                              ? 'rgba(0, 74, 173, 0.15)'
                              : 'rgba(203, 241, 153, 0.3)',
                            border: theme => theme.palette.mode === 'dark'
                              ? '1px solid rgba(0, 74, 173, 0.3)'
                              : '1px solid rgba(162, 195, 164, 0.5)'
                          }}>
                            <Typography variant="caption" sx={{ 
                              color: theme => theme.palette.mode === 'dark' ? '#fdfdfd' : '#004aad', 
                              fontWeight: 600,
                              fontSize: '0.8rem'
                            }}>
                              {virtualStagingWorkflow.credits} kredi
                            </Typography>
                          </Box>
                          <SelectButton
                            size="medium"
                            onClick={() => handleWorkflowSelect(virtualStagingWorkflow)}
                          >
                            Seç
                          </SelectButton>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </WorkflowCard>
              )}

              {/* Render Other Workflows */}
              {otherWorkflows.map((workflow, index) => (
                <React.Fragment key={workflow.id}>
                  {index === 0 && showTutorials && !virtualStagingWorkflow && !fastStagingWorkflow && ( // Adjust tutorial logic if staging card isn't shown first
                    <TutorialTip
                      id="workflowSelection"
                      title="İş Akışı Seçimi"
                      description="Her iş akışı farklı bir mimari görselleştirme aracıdır. İhtiyacınıza en uygun olanı seçin."
                      placement="right"
                      onDismiss={() => setShowTutorials(false)}
                    >
                      {/* Render the first card inside the tutorial tip */}
                      <WorkflowCard selected={workflow.active}>
                        {/* ... Card Content ... (Copy from below) */}
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h3" sx={{ mr: 2, color: theme => theme.palette.mode === 'dark' ? '#20B2AA' : 'primary.main', filter: theme => `drop-shadow(0 2px 8px ${theme.palette.primary.main}40)` }}>
                              {workflow.icon}
                            </Typography>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: theme => theme.palette.mode === 'dark' ? '#fff' : 'primary.main', opacity: 0.95 }}>
                                {workflow.title}
                              </Typography>
                              {workflow.id === 'video-generator' && ( /* Keep badges if needed */
                                <Typography variant="caption" sx={{ display: 'inline-block', backgroundColor: theme.palette.primary.main, color: '#fff', padding: '2px 8px', borderRadius: '12px', fontWeight: 600, ml: 1 }}>
                                  Yeni
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ mb: 2, color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)', lineHeight: 1.6 }}>
                            {workflow.description}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="caption" sx={{ color: theme => theme.palette.mode === 'dark' ? '#fff' : 'primary.main', fontWeight: 500, opacity: 0.9 }}>
                              {workflow.credits} kredi
                            </Typography>
                            <SelectButton size="small" selected={workflow.active} onClick={() => handleWorkflowSelect(workflow)}>
                              {workflow.active ? 'Seçildi' : 'Seç'}
                            </SelectButton>
                          </Box>
                        </CardContent>
                      </WorkflowCard>
                    </TutorialTip>
                  )}
                  
                  {/* Render card normally if not the first tutorial item */}
                  {!(index === 0 && showTutorials && !virtualStagingWorkflow && !fastStagingWorkflow) && (
                    <WorkflowCard selected={workflow.active}>
                      {/* ... Card Content ... (Copy from above or original code) */}
                      <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 3,
                          pb: 2,
                          borderBottom: theme => theme.palette.mode === 'dark' 
                            ? '1px solid rgba(255, 255, 255, 0.1)' 
                            : '1px solid rgba(0, 74, 173, 0.1)'
                        }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            mr: 2,
                            background: theme => theme.palette.mode === 'dark'
                              ? 'linear-gradient(135deg, rgba(0, 74, 173, 0.2) 0%, rgba(0, 74, 173, 0.1) 100%)'
                              : 'linear-gradient(135deg, rgba(203, 241, 153, 0.4) 0%, rgba(162, 195, 164, 0.2) 100%)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                          }}>
                            <Typography variant="h4" sx={{ 
                              color: theme => theme.palette.mode === 'dark' ? '#fdfdfd' : '#004aad',
                              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                            }}>
                              {workflow.icon}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ 
                              fontWeight: 700, 
                              color: theme => theme.palette.mode === 'dark' ? '#fdfdfd' : '#004aad',
                              letterSpacing: '-0.01em'
                            }}>
                              {workflow.title}
                            </Typography>
                            {workflow.id === 'video-generator' && (
                              <Typography variant="caption" sx={{ 
                                display: 'inline-block', 
                                backgroundColor: '#004aad', 
                                color: '#fdfdfd', 
                                padding: '3px 10px', 
                                borderRadius: '12px', 
                                fontWeight: 600, 
                                ml: 1,
                                fontSize: '0.75rem',
                                boxShadow: '0 2px 8px rgba(0, 74, 173, 0.3)'
                              }}>
                                Yeni
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Typography variant="body1" sx={{ 
                          mb: 3, 
                          color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#869d96', 
                          lineHeight: 1.6,
                          fontSize: '0.95rem',
                          minHeight: '70px'
                        }}>
                          {workflow.description}
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          pt: 2,
                          borderTop: theme => theme.palette.mode === 'dark'
                            ? '1px solid rgba(255, 255, 255, 0.05)'
                            : '1px solid rgba(0, 74, 173, 0.08)'
                        }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            backgroundColor: theme => theme.palette.mode === 'dark'
                              ? 'rgba(0, 74, 173, 0.15)'
                              : 'rgba(203, 241, 153, 0.3)',
                            border: theme => theme.palette.mode === 'dark'
                              ? '1px solid rgba(0, 74, 173, 0.3)'
                              : '1px solid rgba(162, 195, 164, 0.5)'
                          }}>
                            <Typography variant="caption" sx={{ 
                              color: theme => theme.palette.mode === 'dark' ? '#fdfdfd' : '#004aad', 
                              fontWeight: 600,
                              fontSize: '0.8rem'
                            }}>
                              {workflow.credits} kredi
                            </Typography>
                          </Box>
                          <SelectButton 
                            size="medium" 
                            selected={workflow.active} 
                            onClick={() => handleWorkflowSelect(workflow)}
                          >
                            {workflow.active ? 'Seçildi' : 'Seç'}
                          </SelectButton>
                        </Box>
                      </CardContent>
                    </WorkflowCard>
                  )}
                </React.Fragment>
              ))}
            </>
          );
        })()}
        {/* Original mapping logic removed and replaced by the IIFE above */}
        {/* {canvasWorkflows.map((workflow, index) => ( ... ))} */}
      </Box> {/* End of Workflow Grid Box */}

      {/* Sample Projects Dialog - Placed correctly after the grid box */}
      <Dialog
        open={sampleProjectsOpen}
        onClose={handleCloseSampleProjects}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(26, 26, 38, 0.95)' 
              : '#fdfdfd',
            backgroundImage: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(0, 74, 173, 0.1) 0%, rgba(26, 26, 38, 0) 100%)'
              : 'linear-gradient(135deg, rgba(203, 241, 153, 0.1) 0%, rgba(162, 195, 164, 0.05) 100%)',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 74, 173, 0.1)',
            overflow: 'hidden'
          }
        }}
      >
        <SampleProjectsGallery 
          onSelectProject={handleSelectSampleProject}
          onClose={handleCloseSampleProjects}
        />
      </Dialog>
    </Box>
  );
});

const WorkflowDirectory = () => {
  return (
    <AuthProvider>
      <WorkflowDirectoryContent />
    </AuthProvider>
  );
};

export default WorkflowDirectory;
